import { init, dispose, registerIndicator } from "klinecharts";
import candles1m from "../data/candle-1m.json";
import candles2m from "../data/candle-2m.json";
import candles5m from "../data/candle-5m.json";
import candles30m from "../data/candle-30m.json";
import candles1d from "../data/candle-1d.json";
import candles15m from "../data/candle-15m.json";
import candles1h from "../data/candle-1h.json";
import candles1w from "../data/candle-1w.json";
import candles1mo from "../data/candle-1mo.json";
import gold1m from "../data/gold/gold-candle-1m.json";
import gold2m from "../data/gold/gold-candle-2m.json";
import gold5m from "../data/gold/gold-candle-5m.json";
import gold15m from "../data/gold/gold-candle-15m.json";
import gold30m from "../data/gold/gold-candle-30m.json";
import gold1h from "../data/gold/gold-candle-1h.json";
import gold1d from "../data/gold/gold-candle-1d.json";
import gold1w from "../data/gold/gold-candle-1w.json";
import gold1mo from "../data/gold/gold-candle-1mo.json";
import {
  applyChartType as applyChartTypeUtil, isLineMode as isLineModeUtil, isStepMode as isStepModeUtil, renderStepOverlay as renderStepOverlayUtil, renderHlcOverlay as renderHlcOverlayUtil, renderScatterOverlay as renderScatterOverlayUtil, renderHistogramOverlay as renderHistogramOverlayUtil,
} from "../utils/chartTypes";
import { createPivotsEngine } from "./pivotEngine";
const native_types = new Set([
  "candle_solid",
  "candle_stroke",
  "candle_up_stroke",
  "candle_down_stroke",
  "ohlc",
]);
const chart_colors = {
  mono: "#444",
  up: "#26a69a",
  down: "#ef5350",
  neutral: "#999",
  pivotHigh: "#2962FF",
  pivotLow: "#9a3225",
};
const watchlist_data = {
  SILVER: {
    "1m": candles1m,
    "2m": candles2m,
    "5m": candles5m,
    "15m": candles15m,
    "30m": candles30m,
    "1h": candles1h,
    "1D": candles1d,
    "1W": candles1w,
    "1M": candles1mo,
  },
  GOLD: {
    "1m": gold1m,
    "2m": gold2m,
    "5m": gold5m,
    "15m": gold15m,
    "30m": gold30m,
    "1h": gold1h,
    "1D": gold1d,
    "1W": gold1w,
    "1M": gold1mo,
  },
};
const normSymbolKey = (s) => String(s || "").trim().toUpperCase();
export function useCandlesChartEngine({ getEl, getProps, emit, nextTick }) {
  const state = {
    chart: null,
    ro: null,
    mainPaneId: "candle_pane",
    baseBars: [],
    bars: [],
    _barIndexByTs: new Map(),
    maRegistered: false,
    onCrosshairCb: null,
    pivotOverlayIds: [],
    pivotDotRegistered: false,
    pivotMarkIds: [],
    pivotIndicatorAdded: false,
    _pivotIndicatorRegistered: false,
    _pivotByTs: new Map(),
    selectedSymbol: "SILVER",
    activeStudies: [],
    activeColumns: [],
    _lastVisibleKey: "",
    _emitRaf: null,
    stepOverlayId: null,
    stepRegistered: false,
    hlcOverlayId: null,
    hlcRegistered: false,
    scatterOverlayId: null,
    scatterRegistered: false,
    histOverlayId: null,
    histRegistered: false,
  };
  let publicApi = null;
  const ctx = new Proxy(
    {},
    {
      get(_, key) {
        const props = getProps?.() || {};
        if (key in props) return props[key];
        if (key in state) return state[key];
        if (publicApi && key in publicApi) return publicApi[key];
        return undefined;
      },
      set(_, key, value) {
        state[key] = value;
        return true;
      },
    }
  );
  const pivots = createPivotsEngine({
    state,
    chart_colors,
    getEl,
  });
  function isLineMode() { return isLineModeUtil(ctx); }
  function isStepMode() { return isStepModeUtil(ctx); }
  function renderStepOverlay() { return renderStepOverlayUtil(ctx); }
  function renderHlcOverlay() { return renderHlcOverlayUtil(ctx); }
  function renderScatterOverlay() { return renderScatterOverlayUtil(ctx); }
  function renderHistogramOverlay() { return renderHistogramOverlayUtil(ctx); }
  function applyChartType() {
    return applyChartTypeUtil(ctx, { nativeTypes: native_types, chartColors: chart_colors });
  }
  function getSourceValue(k, field) {
    const o = Number(k?.open);
    const h = Number(k?.high);
    const l = Number(k?.low);
    const c = Number(k?.close);
    switch (String(field || "close").toLowerCase()) {
      case "open": return o;
      case "high": return h;
      case "low": return l;
      case "close": return c;
      case "hl2": return (h + l) / 2;
      case "hlc3": return (h + l + c) / 3;
      case "hlcc4": return (h + l + c + c) / 4;
      case "ohlc4": return (o + h + l + c) / 4;
      default: return c;
    }
  }
  function normalizeMaType(t) {
    const s = String(t || "simple").trim().toLowerCase();
    if (s === "simple") return "sma";
    if (s === "exponential") return "ema";
    if (s === "weighted") return "wma";
    if (s === "welles wilder") return "rma";
    if (s === "triangular") return "tma";
    if (s === "double exponential") return "dema";
    if (s === "triple exponential") return "tema";
    if (s === "hull") return "hma";
    if (s === "vidya") return "vidya";
    if (s === "variable") return "vidya";
    if (s === "time series") return "tsf";
    return "sma";
  }
  function computeSMA(vals, period) {
    const out = new Array(vals.length).fill(null);
    let sum = 0;
    for (let i = 0; i < vals.length; i++) {
      sum += vals[i];
      if (i >= period) sum -= vals[i - period];
      const denom = Math.min(i + 1, period);
      out[i] = sum / denom;
    }
    return out;
  }
  function computeEMA(vals, period, alpha) {
    const out = new Array(vals.length).fill(null);
    const a = Number.isFinite(alpha) ? alpha : 2 / (period + 1);
    let prev = null;
    for (let i = 0; i < vals.length; i++) {
      const v = vals[i];
      if (prev == null) {
        if (i >= period - 1) {
          const seed = computeSMA(vals.slice(0, i + 1), i + 1)[i];
          prev = seed;
          out[i] = prev;
        }
        continue;
      }
      prev = prev + a * (v - prev);
      out[i] = prev;
    }
    return out;
  }
  function computeWMA(vals, period) {
    const out = new Array(vals.length).fill(null);
    const wSum = (period * (period + 1)) / 2;
    for (let i = period - 1; i < vals.length; i++) {
      let sum = 0;
      let w = 1;
      for (let j = i - period + 1; j <= i; j++) {
        sum += vals[j] * w;
        w++;
      }
      out[i] = sum / wSum;
    }
    return out;
  }
  function computeRMA(vals, period) {
    const out = new Array(vals.length).fill(null);
    const a = 1 / period;
    let prev = null;
    for (let i = 0; i < vals.length; i++) {
      const v = vals[i];
      if (prev == null) {
        if (i >= period - 1) {
          const seed = computeSMA(vals.slice(0, i + 1), i + 1)[i];
          prev = seed;
          out[i] = prev;
        }
        continue;
      }
      prev = prev + a * (v - prev);
      out[i] = prev;
    }
    return out;
  }
  function computeTMA(vals, period) {
    const sma1 = computeSMA(vals, period);
    const vals2 = sma1.map((v) => (v == null ? 0 : v));
    const sma2 = computeSMA(vals2, period);
    return sma2.map((v, i) => (sma1[i] == null ? null : v));
  }
  function computeDEMA(vals, period) {
    const ema1 = computeEMA(vals, period);
    const ema1Vals = ema1.map((v) => (v == null ? 0 : v));
    const ema2 = computeEMA(ema1Vals, period);
    return ema1.map((v, i) => (v == null || ema2[i] == null ? null : 2 * v - ema2[i]));
  }
  function computeTEMA(vals, period) {
    const ema1 = computeEMA(vals, period);
    const ema1Vals = ema1.map((v) => (v == null ? 0 : v));
    const ema2 = computeEMA(ema1Vals, period);
    const ema2Vals = ema2.map((v) => (v == null ? 0 : v));
    const ema3 = computeEMA(ema2Vals, period);
    return ema1.map((v, i) => {
      if (v == null || ema2[i] == null || ema3[i] == null) return null;
      return 3 * v - 3 * ema2[i] + ema3[i];
    });
  }
  function computeHMA(vals, period) {
    const half = Math.max(1, Math.floor(period / 2));
    const sqrtP = Math.max(1, Math.floor(Math.sqrt(period)));
    const wmaHalf = computeWMA(vals, half);
    const wmaFull = computeWMA(vals, period);
    const diff = vals.map((_, i) => {
      if (wmaHalf[i] == null || wmaFull[i] == null) return null;
      return 2 * wmaHalf[i] - wmaFull[i];
    });
    const diffVals = diff.map((v) => (v == null ? 0 : v));
    const hma = computeWMA(diffVals, sqrtP);
    return hma.map((v, i) => (diff[i] == null ? null : v));
  }
  function computeVIDYA(vals, period) {
    const out = new Array(vals.length).fill(null);
    const k = 2 / (period + 1);
    let prev = null;
    for (let i = 0; i < vals.length; i++) {
      if (i < period) continue;
      let up = 0, down = 0;
      for (let j = i - period + 1; j <= i; j++) {
        const ch = vals[j] - vals[j - 1];
        if (ch >= 0) up += ch;
        else down += -ch;
      }
      const denom = up + down;
      const cmo = denom === 0 ? 0 : ((up - down) / denom) * 100;
      const alpha = k * (Math.abs(cmo) / 100);
      const v = vals[i];
      if (prev == null) {
        prev = v;
        out[i] = prev;
        continue;
      }
      prev = prev + alpha * (v - prev);
      out[i] = prev;
    }
    return out;
  }
  function computeTSF(vals, period) {
    const out = new Array(vals.length).fill(null);
    const n = period;
    const sumX = (n * (n + 1)) / 2;
    const sumX2 = (n * (n + 1) * (2 * n + 1)) / 6;
    for (let i = n - 1; i < vals.length; i++) {
      let sumY = 0;
      let sumXY = 0;
      let x = 1;
      for (let j = i - n + 1; j <= i; j++) {
        const y = vals[j];
        sumY += y;
        sumXY += x * y;
        x++;
      }
      const denom = n * sumX2 - sumX * sumX;
      if (denom === 0) continue;
      const b = (n * sumXY - sumX * sumY) / denom;
      const a = (sumY - b * sumX) / n;
      out[i] = a + b * n;
    }
    return out;
  }
  function applyOffset(series, offset) {
    const n = series.length;
    const out = new Array(n).fill(null);
    const off = Number(offset || 0);
    for (let i = 0; i < n; i++) {
      const t = i + off;
      if (t >= 0 && t < n) out[t] = series[i];
    }
    return out;
  }
  function ensureKiteMARegistered() {
    if (state.maRegistered) return;
    try {
      registerIndicator({
        name: "MA",
        shortName: "MA",
        calcParams: [50],
        figures: [{ key: "ma", title: "MA: ", type: "line" }],
        calc: (kLineDataList, indicator) => {
          const period = Math.max(1, Number(indicator?.calcParams?.[0] ?? 50));
          const field = indicator?.extendData?.field || "close";
          const typeRaw = indicator?.extendData?.type || "Simple";
          const type = normalizeMaType(typeRaw);
          const offset = Number(indicator?.extendData?.offset ?? 0);
          const vals = kLineDataList.map((k) => getSourceValue(k, field));
          let ma;
          switch (type) {
            case "ema": ma = computeEMA(vals, period); break;
            case "wma": ma = computeWMA(vals, period); break;
            case "rma": ma = computeRMA(vals, period); break;
            case "tma": ma = computeTMA(vals, period); break;
            case "dema": ma = computeDEMA(vals, period); break;
            case "tema": ma = computeTEMA(vals, period); break;
            case "hma": ma = computeHMA(vals, period); break;
            case "vidya": ma = computeVIDYA(vals, period); break;
            case "tsf": ma = computeTSF(vals, period); break;
            case "sma":
            default: ma = computeSMA(vals, period); break;
          }
          if (offset) ma = applyOffset(ma, offset);
          return ma.map((v) => ({ ma: v == null ? null : v }));
        },
      });
      state.maRegistered = true;
    } catch {
      state.maRegistered = true;
    }
  }
  function toTsMs(dt) {
    if (dt == null) return NaN;
    if (typeof dt === "number") return dt;
    const s = String(dt).trim();
    if (/^\d+$/.test(s)) return Number(s);
    if (s.includes("T")) {
      const t = new Date(s).getTime();
      return Number.isFinite(t) ? t : NaN;
    }
    const [datePart, timePart = "00:00:00"] = s.split(" ");
    const [y, m, d] = datePart.split("-").map(Number);
    const [hh = 0, mm = 0, ss = 0] = timePart.split(":").map(Number);
    const t = new Date(y, (m || 1) - 1, d || 1, hh, mm, ss).getTime();
    return Number.isFinite(t) ? t : NaN;
  }
  function toBars(data) {
    return [...data]
      .sort((a, b) => String(a.openTime).localeCompare(String(b.openTime)))
      .map((d) => ({
        timestamp: toTsMs(d.openTime),
        open: +(d.open ?? d.openPrice),
        high: +(d.high ?? d.highPrice),
        low: +(d.low ?? d.lowPrice),
        close: +(d.close ?? d.closePrice),
        volume: +(d.volume ?? 0),
      }))
      .filter((x) => Number.isFinite(x.timestamp));
  }
  function getBarsForTf(tfKey) {
    const tf = String(tfKey || "1D");
    const symKey = normSymbolKey(state.selectedSymbol);
    const symMap = watchlist_data[symKey] || null;
    const fallback = watchlist_data.SILVER;
    const raw =
      (symMap && symMap[tf]) ||
      (fallback && fallback[tf]) ||
      (fallback && fallback["1D"]) ||
      [];

    return toBars(raw);
  }
  function applyNoPriceLabelStyles() {
    if (!state.chart?.setStyles) return;
    state.chart.setStyles({
      candle: {
        priceMark: {
          show: true,
          high: { show: false },
          low: { show: false },
          last: { show: true, line: { show: true }, text: { show: true } },
        },
        maxMinMark: { show: false },
        highPriceMark: { show: false },
        lowPriceMark: { show: false },
        lastPriceMark: { show: false },
      },
      yAxis: { lastPriceMark: { show: false } },
    });
  }
  function applyCrosshairMode(on) {
    if (!state.chart?.setStyles) return;
    state.chart.setStyles({ crosshair: { show: !!on } });
  }
  function applyCursorStyle() {
    const el = getEl();
    if (!el) return;
    const cursor = getProps()?.crosshairOn ? "crosshair" : "default";
    el.style.cursor = cursor;
    el.querySelectorAll("*").forEach((node) => (node.style.cursor = cursor));
  }
  function resizeChartContainer() {
    const el = getEl();
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const width = el.parentElement?.clientWidth || window.innerWidth;
    const height = window.innerHeight - rect.top;
    el.style.width = width + "px";
    el.style.height = height + "px";
  }
  function onWindowResize() {
    resizeChartContainer();
    state.chart?.resize?.();
    applyCursorStyle();
  }
  function getVisibleWindowBars() {
    if (!state.chart || !Array.isArray(state.bars) || !state.bars.length) return [];
    const el = getEl();
    const w = el?.clientWidth || 0;
    const h = el?.clientHeight || 0;
    if (!w || !h) return [];
    const paneId = state.mainPaneId || "candle_pane";
    const left = state.chart.convertFromPixel?.({ x: 0, y: 0 }, { paneId });
    const right = state.chart.convertFromPixel?.({ x: w - 1, y: 0 }, { paneId });
    const i1 = Number(left?.dataIndex);
    const i2 = Number(right?.dataIndex);
    if (!Number.isFinite(i1) || !Number.isFinite(i2)) {
      return state.bars.slice(Math.max(0, state.bars.length - 100));
    }
    const from = Math.max(0, Math.min(i1, i2));
    const to = Math.min(state.bars.length - 1, Math.max(i1, i2));
    return state.bars.slice(from, to + 1);
  }
  function buildVisibleTableRows() {
    const windowBars = getVisibleWindowBars();
    if (!windowBars.length) return [];
    windowBars.sort((a, b) => a.timestamp - b.timestamp);
    const idxByTs = new Map(state.bars.map((b, i) => [b.timestamp, i]));
    const fullClose = state.bars.map((b) => Number(b.close));
    const fullSeriesByKey = {};
    for (const col of state.activeColumns || []) {
      const key = String(col.key || "");
      if (key.startsWith("ma")) {
        const period = Number(key.replace("ma", "")) || 50;
        fullSeriesByKey[key] = computeSMA(fullClose, period);
        continue;
      }
      fullSeriesByKey[key] = new Array(state.bars.length).fill(null);
    }
    const rows = windowBars.map((bar) => {
      const item = idxByTs.get(bar.timestamp);
      const run = { t: bar.timestamp, o: bar.open, h: bar.high, l: bar.low, c: bar.close, v: bar.volume ?? 0 };
      for (const col of state.activeColumns || []) {
        const key = String(col.key || "");
        run[key] = Number.isFinite(item) ? (fullSeriesByKey[key]?.[item] ?? null) : null;
      }
      return run;
    });
    return rows.reverse();
  }
  function emitVisibleTableData() {
    const windowBars = getVisibleWindowBars();
    if (!windowBars.length) return;
    const first = windowBars[0].timestamp;
    const last = windowBars[windowBars.length - 1].timestamp;
    const key = `${first}:${last}:${(state.activeColumns || []).map((c) => c.key).join("|")}`;
    if (key === state._lastVisibleKey) return;
    state._lastVisibleKey = key;
    emit("table-data", buildVisibleTableRows());
  }
  function subscribeVisibleSync() {
    const tick = () => {
      cancelAnimationFrame(state._emitRaf);
      state._emitRaf = requestAnimationFrame(() => emitVisibleTableData());
    };
    const safeSub = (name) => {
      try { state.chart?.subscribeAction?.(name, tick); } catch (_) { }
    };
    safeSub("onVisibleRangeChange");
    safeSub("onZoom");
    safeSub("onScroll");
    safeSub("onPan");
  }
  function reloadChartData(nextBars) {
    state.bars = nextBars;
    state._barIndexByTs = new Map(state.bars.map((b, i) => [b.timestamp, i]));
    state.chart?.setDataLoader?.({
      getBars: ({ callback }) => callback(state.bars),
    });
    state.chart?.clearData?.();
    state.chart?.refresh?.();
    nextTick(() => {
      pivots.ensurePivotTooltipIndicator();
      const key = getProps()?.chartType?.key;
      if (isLineMode()) {
        pivots.markPivotsOnChart();
      } else if (isStepMode()) {
        pivots.markPivotsOnChart();
        renderStepOverlay();
      } else if (key === "scatter") {
        pivots.markPivotsOnChart();
        renderScatterOverlay();
      } else if (key === "histogram") {
        pivots.markPivotsOnChart();
        renderHistogramOverlay();
      } else if (key === "colored_hlc_bar") {
        pivots.markPivotsOnChart();
        renderHlcOverlay();
      } else {
        pivots.markPivotsOnChart();
      }
      emitVisibleTableData();
    });
  }
  function setActiveStudies(studies) {
    state.activeStudies = Array.isArray(studies) ? studies : [];
    state.activeColumns = state.activeStudies.map((s) => ({
      key: s.key,
      label: s.label,
      name: s.name,
      calcParams: Array.isArray(s.calcParams) ? s.calcParams : [],
      extra: s.extra || {},
    }));
    emit("table-columns", state.activeColumns.map(({ key, label }) => ({ key, label })));
    emitVisibleTableData();
  }
  function setSymbol(ticker) {
    const sym = String(ticker || "").trim();
    if (!sym) return;
    state.selectedSymbol = sym;
    state.chart?.setSymbol?.({ ticker: sym });
    const tfKey = getProps()?.tf?.key ?? "1D";
    state.baseBars = getBarsForTf(tfKey);
    reloadChartData([...state.baseBars]);
    applyChartType();
  }
  function fitContent() {
    if (!state.chart) return;
    try { state.chart.scrollToRealTime?.(); } catch { }
    try { state.chart.scrollToDataIndex?.(state.bars.length - 1); } catch { }
    try { state.chart.scrollToTimestamp?.(state.bars[state.bars.length - 1]?.timestamp); } catch { }
    try { state.chart.resize?.(); } catch { }
    try { state.chart.refresh?.(); } catch { }
  }
  function mount() {
    ensureKiteMARegistered();
    pivots.ensurePivotDotOverlayRegistered();
    resizeChartContainer();
    state.chart = init(getEl());
    state.chart.setScrollEnabled?.(true);
    state.chart.setZoomEnabled?.(true);
    applyNoPriceLabelStyles();
    applyCursorStyle();
    const paneIds = (typeof state.chart.getPaneIds === "function" ? state.chart.getPaneIds() : []) || [];
    state.mainPaneId = paneIds[0] || "candle_pane";
    subscribeVisibleSync();
    emitVisibleTableData();
    state.chart.setStyles?.({
      candle: { tooltip: { showRule: "always" } },
      indicator: { title: { show: true }, tooltip: { showRule: "always" } },
    });
    const { tf } = getProps();
    if (tf?.period) state.chart.setPeriod?.(tf.period);
    state.baseBars = getBarsForTf(tf?.key ?? "1D");
    reloadChartData([...state.baseBars]);
    applyChartType();
    state.onCrosshairCb = (payload) => emit("crosshair-change", payload);
    state.chart?.subscribeAction?.("onCrosshairChange", state.onCrosshairCb);
    state.chart?.subscribeAction?.("onCrosshairMove", state.onCrosshairCb);
    const el = getEl();
    const observedTarget = el?.parentElement || el;
    state.ro = new ResizeObserver(() => {
      resizeChartContainer();
      state.chart?.resize?.();
    });
    if (observedTarget) state.ro.observe(observedTarget);
    window.addEventListener("resize", onWindowResize, { passive: true });
    applyCrosshairMode(!!getProps()?.crosshairOn);
    emit("ready", {
      chart: state.chart,
      rootEl: el,
      getMainPaneId: () => state.mainPaneId,
      getBars: () => state.bars,
      getIndexByTs: (ts) => state._barIndexByTs.get(Number(ts)),
    });
  }
  function onCrosshairPropChange(on) {
    applyCrosshairMode(!!on);
    applyCursorStyle();
  }
  function onTfChange(tfKey) {
    const { tf } = getProps();
    if (tf?.period) state.chart?.setPeriod?.(tf.period);
    state.baseBars = getBarsForTf(tfKey);
    reloadChartData([...state.baseBars]);
    applyChartType();
    resizeChartContainer();
    state.chart?.resize?.();
  }
  function onChartTypeChange() {
    applyChartType();
    nextTick(() => {
      pivots.ensurePivotTooltipIndicator();
      const key = getProps()?.chartType?.key;
      if (key === "step") {
        renderStepOverlay();
        pivots.markPivotsOnChart();
        return;
      }
      if (key === "colored_hlc_bar") {
        renderHlcOverlay();
        pivots.markPivotsOnChart();
        return;
      }
      if (key === "scatter") {
        renderScatterOverlay();
        pivots.markPivotsOnChart();
        return;
      }
      if (key === "histogram") {
        renderHistogramOverlay();
        pivots.markPivotsOnChart();
        return;
      }
      pivots.markPivotsOnChart();
    });
    resizeChartContainer();
    state.chart?.resize?.();
  }
  function unmount() {
    state.ro?.disconnect();
    window.removeEventListener("resize", onWindowResize);
    if (state.chart?.unsubscribeAction && state.onCrosshairCb) {
      state.chart.unsubscribeAction("onCrosshairChange", state.onCrosshairCb);
      state.chart.unsubscribeAction("onCrosshairMove", state.onCrosshairCb);
    }
    if (state.chart) {
      try { dispose(getEl()); } catch { }
    }
    state.chart = null;
  }
  publicApi = {
    mount,
    unmount,
    onCrosshairPropChange,
    onTfChange,
    onChartTypeChange,
    setSymbol,
    setActiveStudies,
    fitContent,
    getBars: () => state.bars,
    getMainPaneId: () => state.mainPaneId,
    isLineMode,
    isStepMode,
    renderStepOverlay,
    renderHlcOverlay,
    renderScatterOverlay,
    renderHistogramOverlay,
    applyChartType,
    getRootEl: () => getEl(),
    resizeChartContainer,
  };
  return publicApi;
}
